import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, ArrowLeft, Globe } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetStep, setResetStep] = useState(1);
  const { login, requestPasswordReset, resetPassword } = useAuth();
  
  const { t, i18n } = useTranslation();

  // Detect current language from URL and update i18n
  React.useEffect(() => {
    const lang = location.pathname.includes('/en') ? 'en' : 'ru';
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [location.pathname, i18n]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success(i18n.language === 'ru' ? t('login.successMessage') : t('login.successMessage'));
      navigate(i18n.language === 'ru' ? '/klkt-segment' : '/klkt-segment');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || t('errorMessages.loginError'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!resetEmail) {
      toast.error(t('errorMessages.enterEmail'));
      return;
    }

    setResetLoading(true);
    try {
      await requestPasswordReset(resetEmail);
      toast.success(t('login.resetSuccessMessage'));
      setResetStep(2);
    } catch (error) {
      console.error('Forgot password error:', error);
      toast.error(error.message || t('errorMessages.resetError'));
    } finally {
      setResetLoading(false);
    }
  };

  const handleVerifyResetCode = async () => {
    if (!resetCode) {
      toast.error(t('errorMessages.enterCode'));
      return;
    }

    setResetStep(3);
  };

  const handleResetPassword = async () => {
    if (!newPassword) {
      toast.error(t('errorMessages.enterPassword'));
      return;
    }

    if (newPassword.length < 6) {
      toast.error(t('errorMessages.passwordLength'));
      return;
    }

    setResetLoading(true);
    try {
      await resetPassword(resetEmail, resetCode, newPassword);
      toast.success(t('login.resetPasswordSuccess'));
      setShowForgotPassword(false);
      setResetStep(1);
      setResetEmail('');
      setResetCode('');
      setNewPassword('');
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error(error.message || t('errorMessages.resetPasswordError'));
    } finally {
      setResetLoading(false);
    }
  };

  const resetForgotPassword = () => {
    setShowForgotPassword(false);
    setResetStep(1);
    setResetEmail('');
    setResetCode('');
    setNewPassword('');
    setResetLoading(false);
  };

  const handleLanguageSwitch = () => {
    const newLang = i18n.language === 'ru' ? 'en' : 'ru';
    i18n.changeLanguage(newLang);
    if (newLang === 'ru') {
      navigate('/login');
    } else {
      navigate('/login/en');
    }
  };

  const backToHomePath = i18n.language === 'ru' ? '/' : '/en';

  return (
    <div className="min-h-screen paper-texture flex items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="flex justify-between items-center mb-8">
          <Link to={backToHomePath} className="inline-flex items-center gap-2 hover:text-[#C41E3A] transition-colors" data-testid="back-home-link">
            <ArrowLeft size={20} />
            {t('login.backToHome')}
          </Link>
          
          {/* Language Switcher */}
          <button
            onClick={handleLanguageSwitch}
            className="flex items-center gap-2 text-lg hover:text-[#C41E3A] transition-colors"
            aria-label={i18n.language === 'ru' ? t('language.switchTo') : t('language.switchTo')}
          >
            <Globe size={20} />
            <span className="hidden sm:inline">
              {i18n.language === 'ru' ? 'EN' : 'RU'}
            </span>
            <span className="sm:hidden">
              {i18n.language === 'ru' ? t('language.en') : t('language.ru')}
            </span>
          </button>
        </div>

        <div className="vintage-border bg-[#F5F1E8] p-8 engraving-texture">
          <div className="text-center mb-8">
            <h1
              className="text-4xl font-bold mb-2"
              style={{ fontFamily: 'Playfair Display, serif' }}
              data-testid="login-title"
            >
              {showForgotPassword ? t('login.forgotPasswordTitle') : t('login.title')}
            </h1>
            <p className="text-gray-600" data-testid="login-subtitle">
              {showForgotPassword ? t('login.forgotPasswordSubtitle') : t('login.subtitle')}
            </p>
          </div>

          {!showForgotPassword ? (
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
              <div>
                <label className="block mb-2 font-semibold" data-testid="email-label">
                  <Mail className="inline mr-2" size={18} />
                  {t('login.emailLabel')}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-black bg-[#F5F1E8] focus:outline-none focus:border-[#C41E3A] transition-colors"
                  placeholder={t('login.emailPlaceholder')}
                  required
                  data-testid="email-input"
                />
              </div>

              <div>
                <label className="block mb-2 font-semibold" data-testid="password-label">
                  <Lock className="inline mr-2" size={18} />
                  {t('login.passwordLabel')}
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-black bg-[#F5F1E8] focus:outline-none focus:border-[#C41E3A] transition-colors"
                  placeholder={t('login.passwordPlaceholder')}
                  required
                  data-testid="password-input"
                />
              </div>

              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-[#C41E3A] font-bold hover:underline text-left"
                data-testid="forgot-password-btn"
              >
                {t('login.forgotPassword')}
              </button>

              <button
                type="submit"
                className="w-full engraved-button engraved-button-red disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? t('login.loading') : t('login.loginButton')}
              </button>
            </form>
          ) : (
            <div className="space-y-6" data-testid="forgot-password-form">
              {/* Step 1: Enter email */}
              {resetStep === 1 && (
                <div className="space-y-4">
                  <div>
                    <label className="block mb-2 font-semibold">
                      <Mail className="inline mr-2" size={18} />
                      {t('login.resetEmailLabel')}
                    </label>
                    <input
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-black bg-[#F5F1E8] focus:outline-none focus:border-[#C41E3A] transition-colors"
                      placeholder={t('login.emailPlaceholder')}
                      required
                      data-testid="reset-email-input"
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={resetForgotPassword}
                      className="flex-1 engraved-button engraved-button-gray"
                    >
                      {t('login.backButton')}
                    </button>
                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      disabled={resetLoading}
                      className="flex-1 engraved-button engraved-button-red disabled:opacity-50"
                    >
                      {resetLoading ? t('login.sendingCode') : t('login.getCodeButton')}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Enter confirmation code */}
              {resetStep === 2 && (
                <div className="space-y-4">
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                    <p className="text-sm text-blue-700" dangerouslySetInnerHTML={{ __html: i18n.language === 'ru'
                      ? t('login.resetCodeSent', { email: resetEmail })
                      : t('login.resetCodeSent', { email: resetEmail }) }} />
                  </div>

                  <div>
                    <label className="block mb-2 font-semibold">
                      {t('login.resetCodeLabel')}
                    </label>
                    <input
                      type="text"
                      value={resetCode}
                      onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="w-full px-4 py-3 border-2 border-black bg-[#F5F1E8] focus:outline-none focus:border-[#C41E3A] transition-colors"
                      placeholder={t('login.resetCodePlaceholder')}
                      maxLength={6}
                      data-testid="reset-code-input"
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setResetStep(1)}
                      className="flex-1 engraved-button engraved-button-gray"
                    >
                      {t('login.backButton')}
                    </button>
                    <button
                      type="button"
                      onClick={handleVerifyResetCode}
                      disabled={!resetCode || resetCode.length !== 6}
                      className="flex-1 engraved-button engraved-button-red disabled:opacity-50"
                    >
                      {t('login.confirmButton')}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Enter new password */}
              {resetStep === 3 && (
                <div className="space-y-4">
                  <div>
                    <label className="block mb-2 font-semibold">
                      <Lock className="inline mr-2" size={18} />
                      {t('login.newPasswordLabel')}
                    </label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-black bg-[#F5F1E8] focus:outline-none focus:border-[#C41E3A] transition-colors"
                      placeholder={t('login.newPasswordPlaceholder')}
                      required
                      minLength={6}
                      data-testid="new-password-input"
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setResetStep(2)}
                      className="flex-1 engraved-button engraved-button-gray"
                    >
                      {t('login.backButton')}
                    </button>
                    <button
                      type="button"
                      onClick={handleResetPassword}
                      disabled={resetLoading || !newPassword || newPassword.length < 6}
                      className="flex-1 engraved-button engraved-button-red disabled:opacity-50"
                    >
                      {resetLoading ? t('login.resetting') : t('login.resetButton')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="mt-6 text-center">
            <p className="text-gray-600" data-testid="register-prompt">
              {showForgotPassword ? t('login.rememberPassword') : t('login.noAccount')}
              {showForgotPassword ? (
                <button
                  onClick={resetForgotPassword}
                  className="text-[#C41E3A] font-bold hover:underline"
                >
                  {t('login.loginHere')}
                </button>
              ) : (
                <Link
                  to={i18n.language === 'ru' ? "/register" : "/register/en"}
                  className="text-[#C41E3A] font-bold hover:underline"
                  data-testid="register-link"
                >
                  {t('login.registerLink')}
                </Link>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;